/**
 * hubPlane — Surface Layer (/01)
 *
 * Single source of truth for the DevHub / UserHub plane model. A user's
 * accessible planes and their default landing plane are derived from the
 * Identity roles via the existing role→capability predicates
 * (`roleNavigation.ts`) — never from the email or any hardcoded identity.
 *
 * `devhub`  → ControlPlane (operator cockpit), canonical route `/control-plane`.
 * `userhub` → TenantPlane ("Workspace"),       canonical route `/tenant-plane`.
 */

import { canOpenDevHub, resolveRoleClass, type RoleClass } from '../../components/navigation/roleNavigation';

export type HubPlane = 'devhub' | 'userhub';

/** Canonical, already-wired route root for each plane. */
export const PLANE_ROOT: Record<HubPlane, string> = {
  devhub: '/control-plane',
  userhub: '/tenant-plane',
};

/** Human label per plane (UI copy only — never an internal architecture name). */
export const PLANE_LABEL: Record<HubPlane, string> = {
  devhub: 'Developer Hub',
  userhub: 'Workspace',
};

/** UserHub baseline: every tenant member role can use the Workspace plane. */
const USERHUB_ROLE_CLASSES: ReadonlySet<RoleClass> = new Set<RoleClass>([
  'owner',
  'admin',
  'developer',
  'client_admin',
  'client_user',
  'readonly_viewer',
]);

/** True when the identity's roles grant the operator (DevHub) capability. */
export function canAccessDevHub(roles: readonly string[]): boolean {
  return canOpenDevHub(resolveRoleClass([...roles]));
}

/** True when the identity's roles grant the Workspace (UserHub) baseline. */
export function canAccessUserHub(roles: readonly string[]): boolean {
  return USERHUB_ROLE_CLASSES.has(resolveRoleClass([...roles]));
}

export function canAccessPlane(roles: readonly string[], plane: HubPlane): boolean {
  return plane === 'devhub' ? canAccessDevHub(roles) : canAccessUserHub(roles);
}

/** The planes this identity may enter, DevHub first when available. */
export function availablePlanes(roles: readonly string[]): HubPlane[] {
  const planes: HubPlane[] = [];
  if (canAccessDevHub(roles)) planes.push('devhub');
  if (canAccessUserHub(roles)) planes.push('userhub');
  // A provisioned-but-unclassified identity still gets the Workspace baseline.
  if (planes.length === 0) planes.push('userhub');
  return planes;
}

/**
 * Deterministic default landing plane. Operators (DevHub-capable) land on
 * DevHub; everyone else on the Workspace. This is the active plane before any
 * explicit user switch — it is what makes an operator land on the operator
 * dashboard instead of a generic Workspace one.
 */
export function defaultPlaneFor(roles: readonly string[]): HubPlane {
  return canAccessDevHub(roles) ? 'devhub' : 'userhub';
}

/** Map the `Identity.defaultPlane` contract value (`control`|`tenant`) from roles. */
export function identityDefaultPlane(roles: readonly string[]): 'control' | 'tenant' {
  return defaultPlaneFor(roles) === 'devhub' ? 'control' : 'tenant';
}

/** Map a pathname to the plane it belongs to (null = plane-agnostic). */
export function planeForPath(pathname: string): HubPlane | null {
  if (pathname === PLANE_ROOT.devhub || pathname.startsWith(`${PLANE_ROOT.devhub}/`)) return 'devhub';
  if (pathname === PLANE_ROOT.userhub || pathname.startsWith(`${PLANE_ROOT.userhub}/`)) return 'userhub';
  return null;
}
